import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForbiddenUserAction } from 'entities/viewer';
import { User } from 'shared/api';
import {
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
	ModalFormProps,
} from 'shared/ui';
import { UnblockUserModel } from '../../model';

export const createUnblockUserModal = (model: UnblockUserModel) => {
	const ModalForm = createConfirmModal(model);

	const UnblockUserModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [user] = useUnit([model.$modalData]);

		return (
			<ModalForm
				ref={ref}
				title={t('unblock-user.title')}
				cancelButtonProps={{
					children: t('unblock-user.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('unblock-user.unblock'),
				}}
				{...props}
			>
				{t('unblock-user.description', { name: user?.name })}
			</ModalForm>
		);
	});

	const UnblockUserModalButton = forwardRef<HTMLButtonElement, ModalFormButtonProps<User>>(
		({ modalData, ...props }, ref) => {
			const { t } = useTranslation();

			const disabled = useForbiddenUserAction(modalData);

			return (
				<ModalForm.Button
					ref={ref}
					data-test-id='unblock-user-button'
					variant='outline'
					leftSection={<CircleSlash2 />}
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					{t('unblock-user.unblock')}
				</ModalForm.Button>
			);
		},
	);

	const UnblockUserModalActionIcon = forwardRef<HTMLButtonElement, ModalFormActionIconProps<User>>(
		({ modalData, ...props }, ref) => {
			const disabled = useForbiddenUserAction(modalData);

			return (
				<ModalForm.ActionIcon
					ref={ref}
					data-test-id='unblock-user-action-icon'
					variant='ghost-destructive'
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					<CircleSlash2 />
				</ModalForm.ActionIcon>
			);
		},
	);

	return Object.assign(UnblockUserModal, {
		Button: UnblockUserModalButton,
		ActionIcon: UnblockUserModalActionIcon,
	});
};
